import { DatePipe, NgOptimizedImage, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatTimepicker,
  MatTimepickerInput,
  MatTimepickerToggle,
} from '@angular/material/timepicker';
import {
  DateAdapter,
  RdsAvatarComponent,
  RdsAvatarImageDirective,
  RdsBadgeComponent,
  RdsCheckboxComponent,
  RdsDateRangeInputComponent,
  RdsDateRangePickerComponent,
  RdsDatepickerToggleComponent,
  RdsEndDateDirective,
  RdsErrorDirective,
  RdsFormFieldComponent,
  RdsIconButtonComponent,
  RdsIconComponent,
  RdsInputDirective,
  RdsLabelDirective,
  RdsLinkComponent,
  RdsPopoverDirective,
  RdsPopoverHeaderDirective,
  RdsPopoverParagraphDirective,
  RdsPopoverTextContentComponent,
  RdsSelectComponent,
  RdsSingleSelectOptionComponent,
  RdsStartDateDirective,
  RdsSwitchComponent,
  RdsTextButtonComponent,
  RdsTooltipDirective,
} from '@rds/angular-components';
import { differenceInMinutes, isAfter } from 'date-fns';
import { switchMap, tap } from 'rxjs';

import {
  BookingRequestDetailsViewModel,
  BookingStatusEnum,
  CustomDateAdapter,
  RoleType,
  RoomBookingFormService,
  RoomLayoutsModel,
  RoomServicesModel,
  dateClass,
} from '@bookly/shared';

import { UserSearchAutocompleteComponent } from '../../../../../../shared/src/lib/users/components/user-search-autocomplete/user-search-autocomplete.component';
import { BookingRequestService } from '../../services/booking-request.service';

@Component({
  selector: 'bad-event-details',
  templateUrl: './event-details.component.html',
  styleUrls: ['./event-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgOptimizedImage,
    RdsAvatarComponent,
    RdsAvatarImageDirective,
    RdsIconComponent,
    ReactiveFormsModule,
    DatePipe,
    MatTimepicker,
    MatTimepickerInput,
    MatTimepickerToggle,
    TitleCasePipe,
    RdsBadgeComponent,
    UserSearchAutocompleteComponent,
    RdsLinkComponent,
    RdsIconButtonComponent,
    RdsPopoverDirective,
    RdsPopoverHeaderDirective,
    RdsPopoverParagraphDirective,
    RdsPopoverTextContentComponent,
    RdsTooltipDirective,
    RdsSwitchComponent,
    RdsInputDirective,
    RdsLabelDirective,
    RdsFormFieldComponent,
    RdsSelectComponent,
    RdsSingleSelectOptionComponent,
    RdsErrorDirective,
    RdsDateRangeInputComponent,
    RdsDatepickerToggleComponent,
    RdsDateRangePickerComponent,
    RdsStartDateDirective,
    RdsEndDateDirective,
    RdsCheckboxComponent,
    RdsTextButtonComponent,
  ],
  providers: [
    RoomBookingFormService,
    { provide: DateAdapter, useClass: CustomDateAdapter },
  ],
})
export class EventDetailsComponent implements OnInit {
  @Input() public set eventDetails(
    value: BookingRequestDetailsViewModel | null
  ) {
    if (value && value !== this.eventDetail) {
      this.availableServices =
        value.roomDetails.manageSpaceConfiguration?.services || [];
      this.#roomBookingFormService.setServicesControls(this.availableServices);
      this.eventDetail = value;
      this.isPastBooking = !isAfter(
        this.eventDetail.bookingDetails.startTime,
        new Date()
      );
      this.#roomBookingFormService.setFormValues(value.bookingDetails);
      this.#roomBookingFormService.setRoomDataValidators(value.roomDetails);
      if (value.roomDetails?.manageSpaceConfiguration?.layouts) {
        const activeLayouts =
          value.roomDetails.manageSpaceConfiguration.layouts.filter(
            layout => layout.active
          );

        if (value.bookingDetails.layout) {
          const bookingLayout = value.bookingDetails.layout;
          this.layoutSelectedTimeDate.set(bookingLayout.layoutSelectedTimeDate);
          const isLayoutActive = activeLayouts.some(
            layout =>
              layout.layoutDexUUID === value.bookingDetails.roomLayoutUUID
          );
          if (!isLayoutActive) {
            activeLayouts.push({
              layoutName: bookingLayout.roomLayoutName + ' (INACTIVE)',
              layoutDexUUID: value.bookingDetails.roomLayoutUUID,
              imageUrl: bookingLayout.imageUrl,
              capacity: {
                min: bookingLayout.capacityMin,
                max: bookingLayout.capacityMax,
              },
              setupTime: {
                before: bookingLayout.setupBeforeMinutes,
                after: bookingLayout.setupAfterMinutes,
              },
              active: false,
            } as RoomLayoutsModel);
          }
        }

        this.layouts.set(activeLayouts);
        this.#roomBookingFormService.setRoomLayoutsValidators(
          activeLayouts,
          this.eventDetail.bookingDetails.roomLayoutUUID
        );
      }
    }
  }

  @Input() public set startDate(date: Date | null) {
    if (date && this.isEditMode()) {
      this.#roomBookingFormService.setEventDateTime(
        date,
        this.eventDetail?.roomDetails,
        differenceInMinutes(
          this.eventDetail.bookingDetails.endTime,
          this.eventDetail.bookingDetails.startTime
        )
      );
    }
  }

  @Input() role: RoleType | undefined;

  @Output() public isEditModeEnabled = new EventEmitter<boolean>();

  readonly #bookingRequestService = inject(BookingRequestService);
  readonly #roomBookingFormService = inject(RoomBookingFormService);
  readonly #destroyRef = inject(DestroyRef);
  protected eventDetail!: BookingRequestDetailsViewModel;
  protected isPastBooking: boolean = false;
  protected readonly layouts = signal<RoomLayoutsModel[]>([]);
  protected readonly hasActiveLayouts = computed(() =>
    this.layouts().some(layout => layout.active)
  );
  protected readonly layoutSelectedTimeDate = signal<string | null>(null);
  protected readonly updatedLayoutIds = computed(() => {
    const selectedTime = this.layoutSelectedTimeDate();
    if (!selectedTime) return new Set<string>();
    const selectedDate = new Date(selectedTime);
    return new Set(
      this.layouts()
        .filter(
          layout =>
            layout.updateTime && new Date(layout.updateTime) > selectedDate
        )
        .map(layout => layout.layoutDexUUID)
    );
  });
  protected readonly minDate: Date = new Date();
  protected readonly isEditMode = this.#roomBookingFormService.isEditMode;
  protected readonly unavailableServices =
    this.#roomBookingFormService.unavailableServices;
  protected readonly BookingStatusEnum = BookingStatusEnum;
  protected readonly RoleType = RoleType;

  protected readonly bookingDetailsForm =
    this.#roomBookingFormService.roomBookingForm;
  protected readonly roomServicesForm =
    this.#roomBookingFormService.roomBookingServicesForm;
  protected readonly dateClass = dateClass;
  protected availableServices: RoomServicesModel[] = [];
  protected readonly customSetupTime = new FormControl<boolean>(false);
  protected readonly bookOnBehalfControl = new FormControl<boolean>({
    value: false,
    disabled: !this.isEditMode(),
  });
  protected readonly onBehalfEmails =
    this.#roomBookingFormService.onBehalfEmails;

  public ngOnInit() {
    this.#roomBookingFormService.setEditMode(false);
    this.#roomBookingFormService.layoutsControlValueChange
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe();
    this.customSetupTime.setValue(
      this.eventDetail?.bookingDetails?.customSetupTimeAfter !== undefined ||
        this.eventDetail?.bookingDetails?.customSetupTimeBefore !== undefined
    );
    this.customSetupTime.disable();
    this.bookOnBehalfControl.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(value => {
        if (value) {
          if (this.onBehalfEmails.length === 0) {
            this.#roomBookingFormService.addOnBehalfPerson();
          }
        } else {
          this.#roomBookingFormService.clearOnBehalfPersons();
        }
        this.bookingDetailsForm.updateValueAndValidity();
      });
    this.customSetupTime.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(value => {
        if (!value) {
          this.bookingDetailsForm.patchValue({
            customSetupTimeBeforeM: null,
            customSetupTimeBeforeH: null,
            customSetupTimeAfterM: null,
            customSetupTimeAfterH: null,
          });
        }
      });
  }

  /**
   * Edit, save and cancel are rendered by the parent view, in the sticky action
   * bar next to Reject and Approve — hence the public surface.
   */
  public get isEditable(): boolean {
    return (
      (this.eventDetail.bookingDetails.status.value ===
        BookingStatusEnum.InProgress ||
        this.eventDetail.bookingDetails.status.value ===
          BookingStatusEnum.Approved) &&
      !this.isPastBooking
    );
  }

  public switchToEditMode() {
    this.#roomBookingFormService.setEditMode(true);
    this.isEditModeEnabled.emit(true);
    this.customSetupTime.enable();
    this.#syncBookOnBehalfControl();
    this.bookOnBehalfControl.enable({ emitEvent: false });
  }

  protected addOnBehalfPerson() {
    this.#roomBookingFormService.addOnBehalfPerson();
  }

  protected removeOnBehalfPerson(index: number) {
    this.#roomBookingFormService.removeOnBehalfPerson(index);
    if (this.onBehalfEmails.length === 0) {
      this.bookOnBehalfControl.setValue(false);
    }
  }

  protected get canAddOnBehalfPerson(): boolean {
    return this.#roomBookingFormService.canAddOnBehalfPerson;
  }

  // the switch only mirrors the form state - toggling it would add or drop
  // on behalf controls
  #syncBookOnBehalfControl() {
    this.bookOnBehalfControl.setValue(this.onBehalfEmails.length > 0, {
      emitEvent: false,
    });
  }

  /** Whether the form is currently in a saveable state. */
  public get canSave(): boolean {
    return this.bookingDetailsForm.valid;
  }

  public save() {
    this.bookingDetailsForm.updateValueAndValidity();
    if (this.bookingDetailsForm.valid) {
      this.#bookingRequestService
        .updateBookingRequest({
          id: this.eventDetail.bookingDetails.id,
          ...this.#roomBookingFormService.createRoomBookingRequestBody(),
        })
        .pipe(
          tap(() => {
            this.#roomBookingFormService.setEditMode(false);
            this.customSetupTime.disable();
            this.bookOnBehalfControl.disable({ emitEvent: false });
            this.isEditModeEnabled.emit(false);
          }),
          switchMap(() =>
            this.#bookingRequestService.loadBookingHistory(
              this.eventDetail.bookingDetails.id
            )
          )
        )
        .subscribe();
    }
  }

  public cancel() {
    this.#roomBookingFormService.setEditMode(false);
    this.isEditModeEnabled.emit(false);
    this.customSetupTime.disable();
    this.bookOnBehalfControl.disable({ emitEvent: false });
    if (this.eventDetail) {
      this.roomServicesForm.reset();
      this.#roomBookingFormService.setFormValues(
        this.eventDetail.bookingDetails
      );
      this.#syncBookOnBehalfControl();
    }
  }
}
